import React, { createContext, useContext, useEffect, useState } from 'react';
import { API_BASE, siteTitle } from '../utils/config';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Injects the gtag.js snippet once, using the org-specific Measurement ID.
// The only org-specific value is the id; the rest of the snippet is identical
// for every org (see the standard Google tag boilerplate).
let gaLoadedId: string | null = null;
function loadGoogleAnalytics(measurementId: string) {
  if (gaLoadedId === measurementId) return; // guard against double-injection
  gaLoadedId = measurementId;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId);
}

export interface OrgInfo {
  org_name: string;
  image: string | null;
  ecommerce_logo: string | null;
  contact_person_name: string | null;
  mobile: string | null;
  email_id: string | null;
  upi_id: string | null;
  ga_measurement_id: string | null;
  social_media_url: { url: string; type: string }[] | null;
  description: { short_description: string | null; motto: string | null } | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  /** Feature flag: Virtual Try-On is shown only when this is explicitly true. */
  tryon: boolean | null;
  extra_fields?: { pd_measurement?: boolean; [key: string]: any } | null;
  ecommerce_headers?: { navLinks?: Array<{ id: string, label: string, filterType: string, filterValue: number }> } | null;
}

const OrgContext = createContext<OrgInfo | null>(null);

export const useOrg = () => useContext(OrgContext);

/**
 * Whether the Virtual Try-On feature is enabled for this org. Opt-in: only true
 * when org_preferences.tryon is explicitly true (null/missing => hidden).
 */
export const useTryonEnabled = (): boolean => {
  const org = useContext(OrgContext);
  return org?.tryon === true;
};

/**
 * Tri-state try-on status so the UI can distinguish "still loading the org" from
 * "feature confirmed off". Returns 'loading' until the org is known, then
 * 'enabled' / 'disabled'. Use this to avoid flashing the "unavailable" empty
 * state while the org_preferences fetch is still in flight.
 */
export const useTryonStatus = (): 'loading' | 'enabled' | 'disabled' => {
  const org = useContext(OrgContext);
  if (org === null) return 'loading';
  return org.tryon === true ? 'enabled' : 'disabled';
};

export const OrgProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [org, setOrg] = useState<OrgInfo | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/org_preferences`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!data?.data) return;
        const info: OrgInfo = data.data;
        const effectiveLogo = info.ecommerce_logo || info.image;
        setOrg({ ...info, image: effectiveLogo, ecommerce_logo: effectiveLogo });

        const logoUrl = effectiveLogo;
        if (logoUrl) {
          document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((el) => {
            (el as HTMLLinkElement).href = logoUrl;
          });
        }
        if (info.org_name) {
          document.title = siteTitle(info.org_name);
          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) metaDesc.setAttribute('content', `${info.org_name} - Premium Eyewear Store`);
        }

        if (info.ga_measurement_id) {
          loadGoogleAnalytics(info.ga_measurement_id);
        }
      })
      .catch(() => {});
  }, []);

  return <OrgContext.Provider value={org}>{children}</OrgContext.Provider>;
};
